import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './CommandPalette.css';

interface CommandAction {
  id: string;
  label: string;
  description?: string;
  icon: string;
  category: string;
  action: () => void;
}

interface CommandPaletteProps {
  onNavigate?: (view: 'chat' | 'management', tab?: string) => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setSelectedIndex(0);
  }, []);

  const actions = useMemo<CommandAction[]>(() => [
    {
      id: 'goto-chat',
      label: 'Chat öffnen',
      description: 'Zum KI-Chat wechseln',
      icon: '',
      category: 'Navigation',
      action: () => { onNavigate?.('chat'); close(); },
    },
    {
      id: 'goto-dashboard',
      label: 'Dashboard öffnen',
      description: 'Zur Übersicht wechseln',
      icon: '⊞',
      category: 'Navigation',
      action: () => { onNavigate?.('management', 'dashboard'); close(); },
    },
    {
      id: 'goto-auftraege',
      label: 'Aufträge öffnen',
      description: 'Auftragsverwaltung anzeigen',
      icon: '',
      category: 'Navigation',
      action: () => { onNavigate?.('management', 'auftraege'); close(); },
    },
    {
      id: 'goto-kunden',
      label: 'Kunden öffnen',
      description: 'Kundenverwaltung anzeigen',
      icon: '',
      category: 'Navigation',
      action: () => { onNavigate?.('management', 'kunden'); close(); },
    },
    {
      id: 'goto-artikel',
      label: 'Artikel öffnen',
      description: 'Artikelverwaltung anzeigen',
      icon: '',
      category: 'Navigation',
      action: () => { onNavigate?.('management', 'artikel'); close(); },
    },
    {
      id: 'goto-lager',
      label: 'Lager öffnen',
      description: 'Lagerbestand anzeigen',
      icon: '',
      category: 'Navigation',
      action: () => { onNavigate?.('management', 'lager'); close(); },
    },
    {
      id: 'goto-fertigung',
      label: 'Fertigung öffnen',
      description: 'Fertigungsaufträge anzeigen',
      icon: '',
      category: 'Navigation',
      action: () => { onNavigate?.('management', 'fertigung'); close(); },
    },
    {
      id: 'goto-bestellungen',
      label: 'Bestellungen öffnen',
      description: 'Einkaufsbestellungen anzeigen',
      icon: '',
      category: 'Navigation',
      action: () => { onNavigate?.('management', 'bestellungen'); close(); },
    },
    {
      id: 'goto-kanban',
      label: 'Kanban Board öffnen',
      description: 'Auftrags-Kanban anzeigen',
      icon: '▦',
      category: 'Navigation',
      action: () => { onNavigate?.('management', 'kanban'); close(); },
    },
    {
      id: 'goto-warnungen',
      label: 'Warnungen öffnen',
      description: 'Aktuelle Warnmeldungen anzeigen',
      icon: '',
      category: 'Navigation',
      action: () => { onNavigate?.('management', 'warnungen'); close(); },
    },
    {
      id: 'goto-dokumente',
      label: 'Dokumente öffnen',
      description: 'Dokumentenverwaltung anzeigen',
      icon: '',
      category: 'Navigation',
      action: () => { onNavigate?.('management', 'dokumente'); close(); },
    },
    {
      id: 'goto-qualitaet',
      label: 'Qualität öffnen',
      description: 'Qualitätssicherung anzeigen',
      icon: '',
      category: 'Navigation',
      action: () => { onNavigate?.('management', 'qualitaet'); close(); },
    },
  ], [onNavigate, close]);

  const filteredActions = query.trim() === ''
    ? actions
    : actions.filter(a => {
        const q = query.toLowerCase();
        return (
          a.label.toLowerCase().includes(q) ||
          (a.description?.toLowerCase().includes(q) ?? false) ||
          a.category.toLowerCase().includes(q)
        );
      });

  // Group by category
  const grouped = filteredActions.reduce<Record<string, CommandAction[]>>((acc, action) => {
    if (!acc[action.category]) acc[action.category] = [];
    acc[action.category].push(action);
    return acc;
  }, {});

  // Keyboard handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMac = ((navigator as any).userAgentData?.platform ?? navigator.platform).toUpperCase().includes('MAC');
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      if (modifier && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, filteredActions.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      filteredActions[selectedIndex]?.action();
      return;
    }
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!open) return null;

  let flatIndex = 0;

  return (
    <div className="cp-overlay" onClick={close}>
      <div className="cp-modal" onClick={e => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className="cp-search-row">
          <span className="cp-search-icon">⌕</span>
          <input
            ref={inputRef}
            className="cp-input"
            type="text"
            placeholder="Befehl suchen..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            aria-label="Befehl suchen"
          />
          <kbd className="cp-esc-hint">ESC</kbd>
        </div>

        <div className="cp-results">
          {filteredActions.length === 0 && (
            <div className="cp-empty">Keine Ergebnisse für "{query}"</div>
          )}
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="cp-group">
              <div className="cp-group-label">{category}</div>
              {items.map(action => {
                const idx = flatIndex++;
                const isSelected = idx === selectedIndex;
                return (
                  <button
                    key={action.id}
                    className={`cp-item ${isSelected ? 'cp-item--selected' : ''}`}
                    onClick={action.action}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <span className="cp-item-icon">{action.icon}</span>
                    <span className="cp-item-content">
                      <span className="cp-item-label">{action.label}</span>
                      {action.description && (
                        <span className="cp-item-desc">{action.description}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="cp-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigieren</span>
          <span><kbd>↵</kbd> ausführen</span>
          <span><kbd>Esc</kbd> schließen</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
